/**
 * FIRST OPEN, piece 1, second half: where ldg:home-paint is really taken.
 *
 * Codex review of PR #1099, P2. The mark used to be taken in HubContent's
 * render body, on the line before it returned the routed content. That is
 * the moment every GATE cleared, which is a real thing to know, but it is
 * not the moment a home is on screen: at that point AnimatedRoutes has not
 * committed, and the lazy chunk behind DancerHome, FamilyCommandCenter,
 * AdminHome, DirectorCard or the staff home content has not even been
 * fetched. So /hub-check reported a number that left out exactly the
 * lazy-load and render delay the beacon exists to measure.
 *
 * This component is rendered as a SIBLING INSIDE each home's own Suspense
 * boundary in Landing.tsx, which is what makes the timing honest: React
 * suspends the WHOLE subtree of a boundary, so nothing in here mounts until
 * that home's chunk has resolved and committed alongside it.
 *
 * Then two nested requestAnimationFrames. The effect runs after the commit
 * but before the browser has painted it; the first frame callback runs just
 * before that paint, and the second runs after it. That second callback is
 * the first instant at which the home is genuinely on screen, and it is
 * where the mark is taken.
 *
 * WHAT IT STILL DOES NOT INCLUDE, said plainly rather than left to be
 * discovered: a home's own DATA. DancerHome fetches its portal after mount
 * and FamilyCommandCenter opens its listeners after mount, so this is
 * "the home is on screen", not "the home is full". The beacon has no mark
 * for that and does not claim one.
 *
 * Renders nothing, holds no state, and is safe to place more than once:
 * markBoot is write once (lib/bootTiming.ts), so whichever home mounts
 * first on this load records the time and every later one is a no-op.
 */
import { useEffect } from 'react';
import { markBoot } from '../lib/bootTiming';

export default function HomePaintMark() {
  useEffect(() => {
    if (typeof requestAnimationFrame !== 'function') {
      // No frame callbacks to wait on (a test renderer, an old webview).
      // Recording it now is later than the old render-body call and is the
      // best this environment can answer, which beats not answering.
      markBoot('ldg:home-paint');
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => markBoot('ldg:home-paint'));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, []);
  return null;
}
